import { useEffect, useMemo, useRef, useState } from 'react';
import type { CalculatePriceMultiRoutesAPI } from '@hero/api';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import { useCalculatePriceApi } from '@hero/api-hooks';
import {
  buildDraftPriceCalcRequest,
  useCurrentUser,
  useOrderDraftStore,
} from '@hero/hooks-shared';

/**
 * Scoped variant of {@link usePublishOrderPricingManager} that reads
 * from an `OrderDraftProvider` instead of the singleton
 * `usePublishOrderStore`. Used by the agent confirmation card and
 * other surfaces that scope a draft per-instance (reorder modal,
 * edit drawer) so multiple cards open at once never share resolver
 * state.
 *
 * Behavior is otherwise identical: debounces resolver calls,
 * writes the response back into the scoped store's `pricePreview`,
 * and seeds vehicle / packing / quantity from the matched Route
 * when the user hasn't picked them yet.
 *
 * MUST be called inside `<OrderDraftProvider>`. The publish page
 * keeps using `usePublishOrderPricingManager`.
 */
export function useOrderDraftPricingManager(): {
  isPending: boolean;
  isError: boolean;
} {
  const { orgId } = useCurrentUser();

  const customerId = useOrderDraftStore(s => s.customer?.customerId);
  const isInternalShipment = useOrderDraftStore(s => s.isInternalShipment);
  const origin = useOrderDraftStore(s => s.origin);
  const destinations = useOrderDraftStore(s => s.destinations);
  const vehicleType = useOrderDraftStore(s => s.load.vehicleType);
  const vehicleTypes = useOrderDraftStore(s => s.load.vehicleTypes);
  const loadOverrides = useOrderDraftStore(s => s.loadOverrides);
  const setPricePreview = useOrderDraftStore(s => s.setPricePreview);
  const applyLoadDefaults = useOrderDraftStore(s => s.applyLoadDefaults);

  const calc = useCalculatePriceApi();

  const request = useMemo<CalculatePriceMultiRoutesAPI['req'] | null>(() => {
    if (!orgId) return null;
    return buildDraftPriceCalcRequest({
      orgId,
      customerId: customerId ?? undefined,
      isInternalShipment,
      origin: origin ? { lat: origin.lat, lng: origin.lng } : null,
      destinations: destinations
        .filter(d => d && d.lat !== 0)
        .map(d => ({ lat: d.lat, lng: d.lng })),
      vehicleType: vehicleType ?? undefined,
      vehicleTypes,
      loadOverrides,
      requirePartyContext: !isInternalShipment,
    });
  }, [orgId, customerId, isInternalShipment, origin, destinations, vehicleType, vehicleTypes, loadOverrides]);

  const signature = useMemo(() => {
    if (!request) return null;
    return JSON.stringify(request);
  }, [request]);

  const lastSignatureRef = useRef<string | null>(null);
  /**
   * False from the moment the draft changes until the matching quote
   * lands — including the 200ms debounce, where `calc.isPending` is
   * still false but the price on screen belongs to the previous route.
   * Callers gate their confirm action on this, so a location edit can't
   * be confirmed against a stale price.
   */
  const [isSettled, setSettled] = useState(true);

  useEffect(() => {
    if (!signature || !request) {
      if (lastSignatureRef.current !== null) {
        setPricePreview(null);
        lastSignatureRef.current = null;
      }
      setSettled(true);
      return;
    }
    if (lastSignatureRef.current === signature) {
      setSettled(true);
      return;
    }

    setSettled(false);
    let cancelled = false;
    const timer = setTimeout(() => {
      calc
        .mutateAsync(request)
        .then(data => {
          if (cancelled) return;
          lastSignatureRef.current = signature;
          setSettled(true);
          setPricePreview(data ?? null);

          const firstLeg = data?.routes?.[0];
          const routeVehicles = firstLeg?.routeVehicles;
          const userPickedVehicle =
            loadOverrides.vehicleType || loadOverrides.vehicleTypes;
          if (
            routeVehicles?.length &&
            !vehicleType &&
            !userPickedVehicle
          ) {
            const derived = resolveLoadPackingAndQuantityFromVehicles(routeVehicles);
            applyLoadDefaults({
              defaults: {
                vehicleTypes: routeVehicles,
                vehicleType: routeVehicles[0],
                ...(!loadOverrides.packing ? { packing: derived.packing } : {}),
                ...(!loadOverrides.quantity ? { quantity: derived.quantity } : {}),
              },
            });
          }
        })
        .catch(() => {
          if (cancelled) return;
          setSettled(true);
          setPricePreview(null);
        });
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return {
    isPending: calc.isPending || !isSettled,
    isError: calc.isError,
  };
}
